import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2, Trash2, Rocket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { Panel, EmptyState } from "@/components/core/AppLayout";
import { ConfirmDialog } from "@/components/core/ConfirmDialog";
import { ChannelPicker } from "@/components/ped/PedBits";
import { CampaignView } from "./CampaignView";

const L = ({ children }) => <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">{children}</div>;
const inWeek = () => { const d = new Date(Date.now() + 7 * 86400e3); return d.toISOString().slice(0, 10); };

const CampaignForm = ({ clientId, onCreated }) => {
  const [f, setF] = useState({ obiettivo: "", budget: "1000", durata: "4 settimane", data_inizio: inWeek(), canali: ["instagram"], target: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const submit = async () => {
    if (f.obiettivo.trim().length < 3) return toast.error("Descrivi l'obiettivo della campagna");
    if (!f.canali.length) return toast.error("Seleziona almeno un canale");
    setBusy(true);
    try {
      const r = await api.post(`/clients/${clientId}/campagne`, { ...f, budget: Number(f.budget) || 0 });
      toast.success("Campagna generata e salvata"); onCreated(r.data);
    } catch (e) { toast.error(errMsg(e, "Generazione non riuscita")); }
    finally { setBusy(false); }
  };
  return (
    <Panel className="p-6 space-y-4" data-testid="campaign-form">
      <h3 className="font-display text-lg font-extrabold text-[#0A1128] flex items-center gap-2"><Sparkles className="h-5 w-5 text-[#E8173A]" />Idee campagna con AI</h3>
      <div><L>Obiettivo *</L><Textarea rows={2} value={f.obiettivo} onChange={set("obiettivo")} placeholder="Es. lanciare il nuovo menù autunnale e aumentare le prenotazioni del weekend" className="rounded-xl" data-testid="campaign-obiettivo" /></div>
      <div className="grid grid-cols-3 gap-3">
        <div><L>Budget (€)</L><Input type="number" min="0" value={f.budget} onChange={set("budget")} className="rounded-xl" data-testid="campaign-budget" /></div>
        <div><L>Durata</L><Input value={f.durata} onChange={set("durata")} className="rounded-xl" data-testid="campaign-durata" /></div>
        <div><L>Data inizio</L><Input type="date" value={f.data_inizio} onChange={set("data_inizio")} className="rounded-xl" data-testid="campaign-data-inizio" /></div>
      </div>
      <div><L>Canali</L><ChannelPicker value={f.canali} onChange={set("canali")} testidPrefix="campaign-canale" /></div>
      <div><L>Target</L><Input value={f.target} onChange={set("target")} placeholder="Es. famiglie e coppie 30-55 anni in zona" className="rounded-xl" data-testid="campaign-target" /></div>
      <Button onClick={submit} disabled={busy} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] font-bold" data-testid="campaign-generate">
        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Sparkles className="h-4 w-4 mr-1.5" />}{busy ? "Sto progettando la campagna..." : "Genera campagna"}
      </Button>
    </Panel>
  );
};

export const ClientCampaigns = ({ client }) => {
  const { data, reload } = useFetch(`/clients/${client.id}/campagne`);
  const [sel, setSel] = useState(null);
  const [del, setDel] = useState(null);
  const current = sel && (data || []).find((c) => c.id === sel);
  const remove = async () => {
    try { await api.delete(`/campagne/${del.id}`); toast.success("Campagna eliminata"); if (sel === del.id) setSel(null); reload(); }
    catch (e) { toast.error(errMsg(e)); }
    setDel(null);
  };
  return (
    <div className="grid grid-cols-[360px_1fr] gap-6" data-testid="client-campaigns">
      <div className="space-y-4">
        <CampaignForm clientId={client.id} onCreated={(c) => { reload(); setSel(c.id); }} />
        <Panel className="p-4" data-testid="campaign-list">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2 px-2">Campagne salvate</div>
          {data && !data.length && <div className="text-sm text-slate-600 px-2 py-3">Nessuna campagna salvata.</div>}
          {(data || []).map((c) => (
            <div key={c.id} className={`flex items-center gap-2 rounded-xl px-2 py-2 cursor-pointer ${sel === c.id ? "bg-[#0A1128] text-white" : "hover:bg-[#A6DAFC] text-[#0A1128]"}`} onClick={() => setSel(c.id)} data-testid={`campaign-item-${c.id}`}>
              <Rocket className="h-4 w-4 shrink-0 text-[#E8173A]" />
              <div className="min-w-0 flex-1"><div className="font-bold text-sm truncate">{c.titolo}</div><div className={`text-xs ${sel === c.id ? "text-white/70" : "text-slate-600"}`}>{fmtDateTime(c.created_at)}</div></div>
              <button onClick={(e) => { e.stopPropagation(); setDel(c); }} className="p-1.5 rounded-lg hover:bg-red-50 text-[#E8173A]" data-testid={`campaign-delete-${c.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </Panel>
      </div>
      {current ? <CampaignView campaign={current} onChange={reload} /> : <Panel className="p-6"><EmptyState icon={Rocket} title="Seleziona o genera una campagna" text="Il piano include concept, budget per canale, fasi, KPI e idee di contenuto da trasformare in post PED." testid="campaign-empty" /></Panel>}
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Eliminare la campagna?" description={`«${del?.titolo}» verrà eliminata. I post già creati nel PED restano.`} onConfirm={remove} />
    </div>
  );
};
